import {useDispatch, useSelector} from 'Provider';
import {CloseIcon, View} from 'native-base';
import React, {useCallback} from 'react';
import {Pressable} from 'react-native';

import OfferDetails from 'components/Offers/OfferDetails';
import {useFavoriteOffer} from 'hooks/useFavoriteOffer';
import {usePropertyList} from 'hooks/usePropertyList';
import {
  OCRMReward,
  OFFER_ACCEPT_ERROR,
  OFFER_HOW_REDEEM_PDB,
  OFFER_OCRM_ERRORSTATE_MESSAGE,
  OFFER_TERMS_PDB,
  OFFER_TERMS_PDB_RIN,
  OfferSource,
  RewardState,
} from 'models/Offer';
import {isWebViewSelector} from 'store/content';
import {OCRM_ACCEPT_STATUS, OffersActions} from 'store/offers/offers.reducer';
import {
  nextOfferIdSelector,
  offersOCRMOfferAcceptSelector,
  offersSaveOfferPrefLoadingSelector,
  previousOfferIdSelector,
  selectedGroupingSelector,
  selectedOfferSelector,
} from 'store/offers/offers.selectors';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

export type ConnectedOfferDetailsProps = {
  handleCloseOfferDetails: () => void;
  ocrmTerms?: string;
  ocrmPostAcceptMessage?: string;
};

const ConnectedOfferDetails: React.FC<ConnectedOfferDetailsProps> = props => {
  const {handleCloseOfferDetails, ocrmPostAcceptMessage, ocrmTerms} = props;
  const dispatch = useDispatch();
  const selectedOffer = useSelector(selectedOfferSelector);
  const ocrmAcceptState = useSelector(offersOCRMOfferAcceptSelector);
  const isWebView = useSelector(isWebViewSelector);
  const {offerProperties} = usePropertyList(selectedOffer?.propertyList);
  const isOcrm = selectedOffer?.source === OfferSource.ocrm;

  const nextOfferId = useSelector(nextOfferIdSelector);
  const previousOfferId = useSelector(previousOfferIdSelector);
  const {trackAdobeEvent} = useTrackEvent();
  const ocrmErrorStateMessage =
    isOcrm &&
    selectedOffer.ocrm.rewards.some(
      reward => reward.state === RewardState.ERROR
    )
      ? OFFER_OCRM_ERRORSTATE_MESSAGE
      : undefined;

  const onNextOfferPress = useCallback(() => {
    dispatch(OffersActions.clearAcceptOCRMOfferState());
    dispatch(OffersActions.setSelectedOfferId(nextOfferId));
  }, [dispatch, nextOfferId]);

  const onPreviousOfferPress = useCallback(() => {
    dispatch(OffersActions.clearAcceptOCRMOfferState());
    dispatch(OffersActions.setSelectedOfferId(previousOfferId));
  }, [dispatch, previousOfferId]);

  const selectedGrouping = useSelector(selectedGroupingSelector);

  const getTermsPDB = () => {
    if (selectedOffer?.propertyList.includes('RIN')) {
      return `${OFFER_TERMS_PDB}\n\n${OFFER_TERMS_PDB_RIN}`;
    }
    return OFFER_TERMS_PDB;
  };

  const acceptOCRMOfferPress = (reward: OCRMReward) => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: 'rewards accept offer',
      },
      rewards: {
        offerType: reward.refinedType,
        offerCode: reward.id,
      },
    });
    dispatch(OffersActions.acceptOCRMOffer(reward));
  };

  const offerPrefLoadingArray = useSelector(offersSaveOfferPrefLoadingSelector);
  const isOfferPrefLoading = selectedOffer
    ? offerPrefLoadingArray?.includes(selectedOffer.id)
    : false;
  const onFavoritePress = useFavoriteOffer(selectedOffer);

  if (selectedOffer) {
    return (
      <OfferDetails
        isOfferPrefLoading={isOfferPrefLoading}
        offer={selectedOffer}
        properties={offerProperties}
        onFavoritePress={onFavoritePress}
        closeSlideFlyout={handleCloseOfferDetails}
        terms={isOcrm ? ocrmTerms || '' : getTermsPDB()}
        pdbHowRedeemText={
          isOcrm || selectedOffer.propertyList.includes('UIN')
            ? undefined
            : OFFER_HOW_REDEEM_PDB
        }
        ocrmPostAcceptMessage={isOcrm ? ocrmPostAcceptMessage : undefined}
        acceptOCRMOfferPress={acceptOCRMOfferPress}
        ocrmAcceptState={ocrmAcceptState}
        ocrmAcceptFailureMessage={
          ocrmAcceptState.status === OCRM_ACCEPT_STATUS.FAILED
            ? OFFER_ACCEPT_ERROR
            : undefined
        }
        ocrmErrorStateMessage={ocrmErrorStateMessage}
        onNextOfferPress={onNextOfferPress}
        onPreviousOfferPress={onPreviousOfferPress}
        hasNextOffer={!!nextOfferId}
        hasPreviousOffer={!!previousOfferId}
        selectedGroupingName={selectedGrouping?.name}
        isWebView={isWebView}
      />
    );
  }
  return (
    <View p="48px">
      <View alignSelf="flex-end">
        <Pressable
          testID="offer-details-close-button"
          onPress={handleCloseOfferDetails}>
          <CloseIcon size="17px" color="tuna" />
        </Pressable>
      </View>
    </View>
  );
};

export default ConnectedOfferDetails;
